import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../store";
import { getCompaniesThunk } from "../../store/actions/company.action";
import { Select, Button, App } from "antd";
import logoImage from "../../assets/images/logo.svg";
import theme from "../../themes";
import { authCardCss, authPageCss } from "../../styles/responsive";
import ToastMessage from "../../utils/toastMessage";
import { switchCompany } from "../../store/actions/auth.action";

const CompanySelection: React.FC = () => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { companies, loading: companiesLoading } = useAppSelector(
    (state) => state.company
  );
  const { user } = useAppSelector((state) => state.auth);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const fetchCompanies = async () => {
    await dispatch(getCompaniesThunk()).unwrap();
  };

  const handleCompanySelect = async () => {
    if (!selectedCompanyId) {
      toast.error("Please select a company", 5);
      return;
    }

    setLoading(true);
    try {
      await dispatch(switchCompany(selectedCompanyId)).unwrap();
      toast.success("Company selected successfully", 5);
      navigate("/");
    } catch (error) {
      toast.error("Failed to select company. Please try again.", 5);
    } finally {
      setLoading(false);
    }
  };

  // Set the current company as the selected company
  useEffect(() => {
    if (companies.length > 0 && user?.company_id) {
      // Find the current company in the companies list
      const currentCompany = companies.find(
        (company) => company._id === user.company_id
      );
      if (currentCompany) {
        setSelectedCompanyId(currentCompany._id);
      } else {
        // Fallback to first company if current company not found
        setSelectedCompanyId(companies[0]?._id || null);
      }
    } else if (companies.length > 0) {
      // Fallback to first company if no user or company_id
      setSelectedCompanyId(companies[0]?._id || null);
    }
  }, [companies, user?.company_id]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  return (
    <Container>
      <LogoContainer>
        <LogoWrapper>
          <LogoImage src={logoImage} alt="Lytte.ai Logo" />
          <Title>Lytte.ai</Title>
        </LogoWrapper>
      </LogoContainer>

      <Card>
        <PageTitle>Select Company</PageTitle>
        <Subtitle>
          Please select the company you want to access. You can switch between
          companies at any time.
        </Subtitle>

        <FormContainer>
          <FormItem>
            <Select
              placeholder="Select a company"
              value={selectedCompanyId}
              onChange={setSelectedCompanyId}
              loading={companiesLoading}
              size="large"
              style={{ width: "100%" }}
              options={companies.map((company) => ({
                label: company.name,
                value: company._id,
              }))}
            />
          </FormItem>

          <ButtonContainer>
            <ContinueButton
              onClick={handleCompanySelect}
              disabled={loading || !selectedCompanyId || companiesLoading}
              loading={loading}
            >
              Continue to Dashboard
            </ContinueButton>
          </ButtonContainer>
        </FormContainer>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  ${authPageCss}
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 640px) {
    margin-bottom: 24px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  ${authCardCss}
`;

const PageTitle = styled.h2`
  color: #333;
  text-align: left;
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: #333;
  text-align: left;
  margin: 0 0 24px 0;
  font-size: 18px;
  line-height: 1.5;
  max-width: 560px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ContinueButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #047857 !important;
    color: #fff !important;
  }

  &:disabled {
    background: ${theme.colors.grey2};
    cursor: not-allowed;
  }
`;

export default CompanySelection;
